import * as React from 'react';
import { MdBusiness, MdLocationOn, MdSearch, MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../../../Components/Table/DataGrid';
import { APPRoutes } from '../../../Utils/Route';
import { useAuth } from '../../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../../Utils/Permissions';
import { getAllProviderProductivityAnalytics } from '../../../Services/AnalyticsService';
import { toast } from 'react-toastify';
import PageTitle from '../../../Components/Common/PageTitle';

export default function AnalyticsProductivityList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canList = permissionHas(userPermissionCodes, PermissionCodes.ANALYTICS_PRODUCTIVITY_LIST, decodedToken);
  const canViewDetails = permissionHas(userPermissionCodes, PermissionCodes.ANALYTICS_PRODUCTIVITY_VIEW, decodedToken);

  const [rows, setRows] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [summary, setSummary] = React.useState({
    organization_count: 0,
    provider_count: 0,
    facility_count: 0,
  });

  const loadProviders = React.useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await getAllProviderProductivityAnalytics(token);
      const items = response?.data?.providers || [];
      setRows(Array.isArray(items) ? items : []);
      setSummary({
        organization_count: Number(response?.data?.organization_count) || 0,
        provider_count: Number(response?.data?.provider_count) || 0,
        facility_count: Number(response?.data?.facility_count) || 0,
      });
    } catch (error) {
      console.error('Error loading analytics productivity:', error);
      setRows([]);
      setSummary({ organization_count: 0, provider_count: 0, facility_count: 0 });
      toast.error('Unable to load productivity analytics.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const name = (row.provider_name || '').toLowerCase();
      const org = (row.organization_name || '').toLowerCase();
      return !term || name.includes(term) || org.includes(term);
    });
  }, [rows, search]);

  const stats = React.useMemo(
    () => [
      {
        label: 'Total providers',
        value: summary.provider_count,
        icon: <MdBusiness size={22} color="var(--hc-brand-ink)" />,
        iconBg: '#e6f6f7',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Total Facilities',
        value: summary.facility_count,
        icon: <MdLocationOn size={22} color="#f5b942" />,
        iconBg: '#fef6e7',
        accentClass: 'border-warning',
        valueClass: 'text-warning',
      },
    ],
    [summary],
  );

  const goToProvider = React.useCallback((row) => {
    if (!row || row.is_unmatched || !row.organization_id) return;
    navigate(
      APPRoutes.ANALYTICS_PRODUCTIVITY_VIEW
        .replace(':organization_id', row.organization_id)
        .replace(':provider_name', encodeURIComponent(row.provider_name)),
      { state: { provider: row, organization_id: row.organization_id } },
    );
  }, [navigate]);

  const columns = React.useMemo(
    () => [
      {
        id: 'Provider',
        label: 'Provider',
        minWidth: 220,
        format: (_value, row) => row.is_unmatched ? (
          <span className="text-secondary fst-italic">{row.provider_name}</span>
        ) : canViewDetails ? (
          <button
            type="button"
            className="btn p-0 text-start text-dark border-0 bg-transparent"
            style={{ font: 'inherit' }}
            onClick={() => goToProvider(row)}
          >
            {row.provider_name || '—'}
          </button>
        ) : (
          <span>{row.provider_name || '—'}</span>
        ),
      },
      {
        id: 'Organization',
        label: 'Organization',
        minWidth: 200,
        format: (_value, row) => row.organization_name || <span className="text-secondary">—</span>,
      },
      {
        id: 'Facilities',
        label: 'Facilities',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => (row.facilities || []).length,
      },
      {
        id: 'Visits',
        label: 'Visits',
        minWidth: 110,
        align: 'center',
        format: (_value, row) => row.visit_count ?? 0,
      },
      ...(canViewDetails ? [{
        id: 'actions',
        label: 'Action',
        minWidth: 110,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {!row.is_unmatched ? (
              <button
                type="button"
                title="View"
                className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
                onClick={() => goToProvider(row)}
              >
                <MdVisibility size={16} />
              </button>
            ) : null}
          </div>
        ),
      }] : []),
    ],
    [canViewDetails, goToProvider],
  );

  return (
    <div>
      <PageTitle title="Productivity - Aegis 360" />
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Analytics Productivity</h4>
          <p className="text-secondary mb-0 small">
            View productivity analytics for every provider across your organizations.
          </p>
        </div>
      </div>


      <div className="row g-3 mb-3">
        {stats.map((stat) => (
          <div key={stat.label} className="col-12 col-sm-6 col-lg-6 col-xl">
            <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
              <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-3"
                style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
              >
                {stat.icon}
              </div>
              <div className="d-flex flex-column fw-bold lh-sm">
                <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                  {stat.label}
                </span>
                <span className={`fw-bold ${stat.valueClass}`} style={{ fontSize: '1.25rem' }}>
                  {stat.value}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3 align-items-center">
          <div className="col-12">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search by provider or organization..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', height: '40px', width: '100%' }}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="mt-3">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="d-flex gap-3 align-items-center mb-3">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '25%' }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '15%' }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '15%' }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '15%' }} />
              </div>
            ))}
          </div>
        ) : (
          <DataGrid
            columns={columns}
            rows={filteredRows}
            defaultRowsPerPage={10}
            stickyHeader
            getRowId={(row) => `${row.organization_id}-${row.provider_name}`}
          />
        )}
      </div>
    </div>
  );
}
